import { useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { contactInfo } from "@/mocks/site";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const FORM_ID = "db6c62kbe24agvuu3i20";
const FORM_ACTION = "https://readdy.ai/api/form/db6c62kbe24agvuu3i20";

const contactCards = [
  { icon: "ri-mail-line", label: "邮箱", value: contactInfo.email, href: `mailto:${contactInfo.email}` },
  { icon: "ri-phone-line", label: "电话", value: contactInfo.phone },
  { icon: "ri-map-pin-line", label: "地址", value: contactInfo.address },
  { icon: "ri-time-line", label: "服务时间", value: contactInfo.hours },
];

export default function Contact() {
  useDocumentMeta(
    "联系我们 - 51动漫",
    "通过邮箱、电话或在线留言表单联系 51动漫团队。我们欢迎内容建议、合作洽谈与反馈意见。"
  );

  const [values, setValues] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 反垃圾：蜜罐字段，正常用户应为空
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setValues({ name: "", email: "", message: "" });
      form.reset();
      return;
    }

    setSubmitting(true);
    setStatus("idle");
    setFormError("");

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "website_alt") return;
        const str = String(value);
        if (str.trim() !== "") payload.append(key, str);
      });

      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isOk = response.ok && parsed?.code === "OK";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (isOk && !isSpam) {
        setStatus("success");
        setValues({ name: "", email: "", message: "" });
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />

      <div className="mt-5 mb-8 max-w-3xl">
        <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 mb-3">
          联系我们
        </h1>
        <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
          无论是内容建议、合作洽谈，还是想加入我们，都欢迎随时联系。你也可以直接填写下方留言表单，我们会尽快回复。
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 md:gap-12">
        {/* Contact info */}
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {contactCards.map((card) => (
              <div
                key={card.label}
                className="rounded-lg bg-background-100 border border-background-200 p-5"
              >
                <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center mb-3">
                  <i className={`${card.icon} text-xl`}></i>
                </span>
                <div className="text-xs text-foreground-500 mb-1">{card.label}</div>
                {card.href ? (
                  <a
                    href={card.href}
                    className="text-sm font-semibold text-foreground-950 hover:text-primary-600 transition-colors break-all"
                  >
                    {card.value}
                  </a>
                ) : (
                  <div className="text-sm font-semibold text-foreground-950">{card.value}</div>
                )}
              </div>
            ))}
          </div>

          <div className="rounded-lg overflow-hidden border border-background-200 h-64 md:h-72">
            <iframe
              title="51动漫 办公地址地图"
              src="https://www.google.com/maps?q=Hangzhou%20Binjiang%20District&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>

        {/* Form */}
        <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
          <h2 className="font-heading font-bold text-xl text-foreground-950 mb-1">
            在线留言
          </h2>
          <p className="text-sm text-foreground-500 mb-6">
            填写你的联系方式与想说的话，我们会在 1-2 个工作日内回复。
          </p>

          {status === "success" && (
            <div className="mb-5 rounded-md bg-secondary-100 border border-secondary-200 text-secondary-900 px-4 py-3 text-sm flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-lg"></i>
              提交成功！感谢你的留言，我们会尽快与你联系。
            </div>
          )}
          {status === "error" && (
            <div className="mb-5 rounded-md bg-accent-100 border border-accent-200 text-accent-900 px-4 py-3 text-sm flex items-center gap-2">
              <i className="ri-error-warning-line text-lg"></i>
              {formError}
            </div>
          )}

          <form
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                姓名 <span className="text-accent-500">*</span>
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                value={values.name}
                onChange={handleChange}
                placeholder="请输入你的称呼"
                className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                邮箱 <span className="text-accent-500">*</span>
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                value={values.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                留言内容 <span className="text-accent-500">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                value={values.message}
                onChange={handleChange}
                placeholder="请填写你想咨询或反馈的内容（最多 500 字）"
                className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 resize-none"
              ></textarea>
              <div className="mt-1 text-right text-xs text-foreground-400">
                {values.message.length}/500
              </div>
            </div>

            {/* 反垃圾蜜罐字段，正常用户不可见、不填写 */}
            <input
              type="text"
              name="website_alt"
              className="form-note-anchor"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  提交中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill"></i>
                  提交留言
                </>
              )}
            </button>
            <p className="text-xs text-foreground-400 text-center">
              提交即表示你同意我们按隐私政策使用你填写的信息。
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}